import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  FormSection,
  MasterDataForm,
} from '../components/master-data/MasterDataForm';
import {
  FilterConfig,
  MasterDataTable,
  TableColumn,
} from '../components/master-data/MasterDataTable';

import { ImportExportProgress } from '@/components/ui/ImportExportProgress';
import { useImportExport } from '@/hooks/useImportExport';
import { buildCsv } from '@/lib/csv';
import { useMemo, useState } from 'react';
import { Badge } from '../components/ui/badge';
import { useAuth } from '../hooks/useAuth';
import { useToast } from '../hooks/useToast';

interface Customer {
  _id: string;
  name: string;
  code?: string;
  accountCode?: string;
  contactNo?: string;
  company?: string | { _id: string; name: string };
  originHub?: string | { _id: string; name: string };
  parentCustomer?: string;
  originCode?: string;
  email?: string;
  pan?: string;
  billingEmail?: string;
  operationalEmail?: string;
  misEmail?: string;
  misCcEmail?: string;
  pincode?: string;
  city?: string;
  state?: string;
  country?: string;
  dialCode?: string;
  contactNumber?: string;
  customerType?: 'DIRECT' | 'COCOURIER' | 'OUTSTATION' | 'OTHER' | 'FRANCHISEE' | 'BRANCH' | 'AGENT' | 'IATA' | 'E_COMMERCE' | 'BRANCHES' | 'SECURITY_DEPOSITS' | 'IMPORT' | 'EXPORTER' | 'US_LOCAL' | 'CORPORATE' | '99_ACCOUNT' | 'KEY_ACCOUNT';
  customerCategory:
    | 'INDIVIDUAL'
    | 'COMPANY'
    | 'PARTNERSHIP'
    | 'LLP'
    | 'SOLE_PROPRIETOR';
  address?: string;
  customerInstruction?: string;
  currency?: string;
  placeOfSupply?: 'SAME' | 'DIFFERENT';
  salesman1?: string;
  salesman2?: string;
  salesman3?: string;
  csPerson?: string;
  billingPerson?: string;
  invoicePerson?: string;
  billingCycle?: string;
  invoiceHead?: 'CASH_INVOICE' | 'INVOICE';
  paymentType?: 'CASH' | 'CHEQUE' | 'ONLINE' | 'CREDIT' | 'PREPAID' | 'COD';
  adcNoc?: string;
  gstNumber?: string;
  eoriNumber?: string;
  iec?: string;
  iecBranchCode?: string;
  bankAccountNumber?: string;
  bankAdCode?: string;
  tdsPercentage?: number;
  depositAmount?: number;
  creditLimit?: number;
  agingLimit?: number;
  contract?: string;
  rateModifierHead?: string;
  assignedHubs?: string[];
  kycDocuments?: string[];
  status: 'ACTIVE' | 'INACTIVE';
  createdAt: string;
  updatedAt: string;
}

const customerTypes = [
  { value: 'DIRECT', label: 'Direct' },
  { value: 'COCOURIER', label: 'Cocourier' },
  { value: 'OUTSTATION', label: 'Outstation' },
  { value: 'OTHER', label: 'Other' },
  { value: 'FRANCHISEE', label: 'Franchisee' },
  { value: 'BRANCH', label: 'Branch' },
  { value: 'AGENT', label: 'Agent' },
  { value: 'IATA', label: 'IATA' },
  { value: 'E_COMMERCE', label: 'E-Commerce' },
  { value: 'BRANCHES', label: 'Branches' },
  { value: 'SECURITY_DEPOSITS', label: 'Security Deposits' },
  { value: 'IMPORT', label: 'Import' },
  { value: 'EXPORTER', label: 'Exporter' },
  { value: 'US_LOCAL', label: 'US Local' },
  { value: 'CORPORATE', label: 'Corporate' },
  { value: '99_ACCOUNT', label: '99 Account' },
  { value: 'KEY_ACCOUNT', label: 'Key Account' },
];

const customerCategories = [
  { value: 'INDIVIDUAL', label: 'Individual' },
  { value: 'COMPANY', label: 'Company' },
  { value: 'PARTNERSHIP', label: 'Partnership' },
  { value: 'LLP', label: 'LLP' },
  { value: 'SOLE_PROPRIETOR', label: 'Sole Proprietor' },
];

const customerTypeLabelMap = Object.fromEntries(
  customerTypes.map((option) => [option.value, option.label])
);
const customerCategoryLabelMap = Object.fromEntries(
  customerCategories.map((option) => [option.value, option.label])
);

const paymentMethods = [
  { value: 'CASH', label: 'Cash' },
  { value: 'CHEQUE', label: 'Cheque' },
  { value: 'ONLINE', label: 'Online' },
  { value: 'CREDIT', label: 'Credit' },
  { value: 'PREPAID', label: 'Prepaid' },
  { value: 'COD', label: 'COD' },
];

const mapDropdownOption = (option: any) => ({
  _id: option.value,
  name: option.label,
  code: option.code,
  symbol: option.symbol,
  dialCode: option.dialCode,
  type: option.type,
});

const CustomerManagement = () => {
  const queryClient = useQueryClient();
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [editingCustomer, setEditingCustomer] = useState<Customer | null>(null);
  const { toast } = useToast();
  const { axios } = useAuth();
  const { importData, exportData, importProgress, exportProgress } =
    useImportExport();

  // State for server-side pagination, filtering, and searching
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [searchTerm, setSearchTerm] = useState('');
  const [currentFilters, setCurrentFilters] = useState<Record<string, string>>({});
  const [sortConfig, setSortConfig] = useState<{ sortBy?: string; sortOrder?: 'asc' | 'desc' }>({});

  // Fetch customers with server-side filtering and pagination
  const {
    data: customersResponse,
    isLoading,
    error,
  } = useQuery({
    queryKey: ['customers', currentPage, pageSize, searchTerm, currentFilters, sortConfig],
    queryFn: async () => {
      const params = new URLSearchParams();
      params.append('page', currentPage.toString());
      params.append('limit', pageSize.toString());

      const normalizedSearch = searchTerm.trim();
      if (normalizedSearch) {
        params.append('search', normalizedSearch);
      }

      Object.entries(currentFilters).forEach(([key, value]) => {
        if (!value) return;
        if (value === 'all') {
          if (key === 'status') {
            params.append(key, value);
          }
          return;
        }
        params.append(key, value);
      });

      if (sortConfig.sortBy) {
        params.append('sortBy', sortConfig.sortBy);
        params.append('sortOrder', sortConfig.sortOrder || 'asc');
      }

      const response = await axios.get(`/api/master-data/customer?${params.toString()}`);
      const result = response.data?.data || {};
      const paginationData = result.pagination || {};

      return {
        data: result.data || [],
        pagination: {
          total: paginationData.total || 0,
          page: paginationData.page || currentPage,
          limit: paginationData.limit || pageSize,
          totalPages: paginationData.totalPages || 1,
        },
      };
    },
    placeholderData: keepPreviousData,
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
    retry: 1,
  });

  const customers = customersResponse?.data || [];
  const pagination = customersResponse?.pagination;

  // Fetch companies for dropdown
  const { data: companies = [] } = useQuery({
    queryKey: ['companies-dropdown'],
    queryFn: async () => {
      try {
        const response = await axios.get('/api/master-data/company/dropdown');
        const options = response.data?.data || [];
        return options.map(mapDropdownOption);
      } catch (error) {
        console.error('Error fetching companies:', error);
        return [];
      }
    },
  });

  // Fetch hubs for dropdown
  const { data: hubs = [] } = useQuery({
    queryKey: ['hubs-dropdown'],
    queryFn: async () => {
      try {
        const response = await axios.get('/api/master-data/hub/dropdown');
        const options = response.data?.data || [];
        return options.map(mapDropdownOption);
      } catch (error) {
        console.error('Error fetching hubs:', error);
        return [];
      }
    },
  });

  // Fetch customers for parent customer dropdown
  const { data: customerOptions = [] } = useQuery({
    queryKey: ['customers-dropdown'],
    queryFn: async () => {
      try {
        const response = await axios.get('/api/master-data/customer/dropdown');
        const options = response.data?.data || [];
        return options.map(mapDropdownOption);
      } catch (error) {
        console.error('Error fetching customer options:', error);
        return [];
      }
    },
  });

  // Fetch currencies for dropdown
  const { data: currencies = [] } = useQuery({
    queryKey: ['currencies-dropdown'],
    queryFn: async () => {
      try {
        const response = await axios.get('/api/master-data/currency/dropdown');
        const options = response.data?.data || [];
        return options.map(mapDropdownOption);
      } catch (error) {
        console.error('Error fetching currencies:', error);
        return [];
      }
    },
  });

  // Fetch countries for dropdown (for originCode field)
  const { data: countries = [] } = useQuery({
    queryKey: ['countries-dropdown'],
    queryFn: async () => {
      try {
        const response = await axios.get('/api/master-data/country/dropdown');
        const options = response.data?.data || [];
        return options.map(mapDropdownOption);
      } catch (error) {
        console.error('Error fetching countries:', error);
        return [];
      }
    },
  });

  // Fetch users for dropdown (for salesman, CS person, billing person, invoice person)
  const { data: users = [] } = useQuery({
    queryKey: ['users-dropdown'],
    queryFn: async () => {
      try {
        const response = await axios.get('/api/users', {
          params: {
            page: 1,
            limit: 200,
            sortBy: 'name',
            sortOrder: 'asc',
          },
        });
        return response.data?.data?.data || [];
      } catch (error) {
        console.error('Error fetching users:', error);
        return [];
      }
    },
  });

  // Fetch KYC types for dynamic checkboxes
  const { data: kycTypes = [] } = useQuery({
    queryKey: ['kyc-dropdown'],
    queryFn: async () => {
      try {
        const response = await axios.get('/api/master-data/kyc/dropdown');
        const options = response.data?.data || [];
        return options.map(mapDropdownOption);
      } catch (error) {
        console.error('Error fetching KYC types:', error);
        return [];
      }
    },
  });

  // Create customer mutation
  const createCustomerMutation = useMutation({
    mutationFn: async (data: any) => {
      const response = await axios.post('/api/master-data/customer', data);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['customers'] });
      queryClient.invalidateQueries({ queryKey: ['customers-dropdown'] });
      toast({
        title: 'Customer created',
        description: 'Customer has been created successfully.',
      });
      setIsFormOpen(false);
      setEditingCustomer(null);
    },
    onError: (error: any) => {
      toast({
        title: 'Error',
        description:
          error.response?.data?.error?.message || 'Failed to create customer',
        variant: 'destructive',
      });
    },
  });

  // Update customer mutation
  const updateCustomerMutation = useMutation({
    mutationFn: async (data: any) => {
      const { _id, ...updateData } = data;
      const response = await axios.put(`/api/master-data/customer/${_id}`, updateData);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['customers'] });
      queryClient.invalidateQueries({ queryKey: ['customers-dropdown'] });
      toast({
        title: 'Customer updated',
        description: 'Customer has been updated successfully.',
      });
      setIsFormOpen(false);
      setEditingCustomer(null);
    },
    onError: (error: any) => {
      toast({
        title: 'Error',
        description:
          error.response?.data?.error?.message || 'Failed to update customer',
        variant: 'destructive',
      });
    },
  });

  // Delete customer mutation (hard delete)
  const deleteCustomerMutation = useMutation({
    mutationFn: async (customerId: string) => {
      await axios.delete(`/api/master-data/customer/${customerId}?soft=false`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['customers'] });
      queryClient.invalidateQueries({ queryKey: ['customers-dropdown'] });
      toast({
        title: 'Customer deleted',
        description: 'Customer has been deleted successfully.',
      });
    },
    onError: (error: any) => {
      toast({
        title: 'Error',
        description:
          error.response?.data?.error?.message || 'Failed to delete customer',
        variant: 'destructive',
      });
    },
  });

  // Bulk delete mutation
  const bulkDeleteMutation = useMutation({
    mutationFn: async (customerIds: string[]) => {
      await axios.post('/api/master-data/customer/bulk-delete', {
        ids: customerIds,
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['customers'] });
      queryClient.invalidateQueries({ queryKey: ['customers-dropdown'] });
      toast({
        title: 'Customers deleted',
        description: 'Selected customers have been deleted successfully.',
      });
    },
    onError: (error: any) => {
      toast({
        title: 'Error',
        description:
          error.response?.data?.error?.message || 'Failed to delete customers',
        variant: 'destructive',
      });
    },
  });

  const columns: TableColumn<Customer>[] = [
    {
      key: 'code',
      label: 'Code',
      sortable: true,
      width: 'w-24',
    },
    {
      key: 'name',
      label: 'Name',
      sortable: true,
    },
    {
      key: 'email',
      label: 'Email',
      sortable: true,
      hideOnMobile: true,
    },
    {
      key: 'contactNo',
      label: 'Contact',
      sortable: true,
      hideOnTablet: true,
    },
    {
      key: 'company',
      label: 'Company',
      hideOnMobile: true,
      render: value => {
        if (typeof value === 'object' && value?.name) {
          return value.name;
        }
        return value || '-';
      },
    },
    {
      key: 'customerType',
      label: 'Type',
      hideOnMobile: true,
      render: value => {
        return customerTypeLabelMap[value] || value || '-';
      },
    },
    {
      key: 'customerCategory',
      label: 'Category',
      hideOnTablet: true,
      render: value => {
        return customerCategoryLabelMap[value] || value || '-';
      },
    },
    {
      key: 'status',
      label: 'Status',
      render: value => (
        <Badge
          className={
            value === 'ACTIVE'
              ? 'bg-green-100 text-green-800'
              : 'bg-red-100 text-red-800'
          }
        >
          {value === 'ACTIVE' ? 'Active' : 'Inactive'}
        </Badge>
      ),
    },
  ];

  const filters: FilterConfig[] = [
    {
      key: 'customerType',
      label: 'Customer Type',
      type: 'select',
      options: [{ value: 'all', label: 'All Types' }, ...customerTypes],
    },
    {
      key: 'customerCategory',
      label: 'Category',
      type: 'select',
      options: [{ value: 'all', label: 'All Categories' }, ...customerCategories],
    },
    {
      key: 'status',
      label: 'Status',
      type: 'select',
      options: [
        { value: 'all', label: 'All Statuses' },
        { value: 'ACTIVE', label: 'Active' },
        { value: 'INACTIVE', label: 'Inactive' },
      ],
    },
    {
      key: 'city',
      label: 'City',
      type: 'text',
      placeholder: 'Filter by city',
    },
  ];

  // User options for dropdowns
  const userOptions = useMemo(() => 
    users.map((user: any) => ({
      value: user._id,
      label: `${user.name || 'Unknown'} (${user.email || 'N/A'})`,
    })),
    [users]
  );

  // Hub options for multi-select
  const hubOptions = useMemo(() => 
    hubs.map((hub: any) => ({
      value: hub._id,
      label: `${hub.name || 'Unknown Hub'} (${hub.code || 'N/A'})`,
    })),
    [hubs]
  );

  // File download handler for customer files
  const downloadCustomerFile = async (customer: Customer, fileType: string) => {
    try {
      const response = await axios.get(
        `/api/master-data/customer/${customer._id}/file/${fileType}`,
        { responseType: 'blob' }
      );
      
      const blob = new Blob([response.data]);
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `${customer.code || 'customer'}_${fileType}`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error(`Error downloading ${fileType}:`, error);
      toast({
        title: 'Download Error',
        description: `Failed to download ${fileType} file.`,
        variant: 'destructive',
      });
    }
  };

  // Build form sections with dynamic data
  const getFormSections = (): FormSection[] => [
    {
      title: 'Basic Information',
      description: 'Customer basic details and contact information',
      fields: [
        {
          name: 'name',
          label: 'Customer Name',
          type: 'text',
          required: true,
          placeholder: 'Enter customer name',
          description: 'Unique name of the Customer',
        },
        {
          name: 'code',
          label: 'Customer Code',
          type: 'text',
          required: false,
          placeholder: 'Enter customer code (optional, auto-generated if empty)',
          description: 'Short unique code for the Customer',
        },
        {
          name: 'accountCode',
          label: 'Account Code',
          type: 'text',
          required: false,
          placeholder: 'Enter account code',
          description: 'Account code of the Customer',
        },
        {
          name: 'email',
          label: 'Email Address',
          type: 'email',
          required: false,
          placeholder: 'Enter email address',
          description: 'Email contact for the Customer',
        },
        {
          name: 'contactNo',
          label: 'Contact Number',
          type: 'tel',
          required: false,
          placeholder: 'Enter contact number',
          description: 'Phone number of the Company',
        },
        {
          name: 'contactNumber',
          label: 'Alternative Contact',
          type: 'tel',
          required: false,
          placeholder: 'Enter alternative contact number',
          description: 'Phone number of the Customer',
        },
        {
          name: 'dialCode',
          label: 'Dial Code',
          type: 'text',
          required: false,
          placeholder: 'Enter dial code (e.g., +91)',
          description: 'Dial code where the Customer is located',
        },
        {
          name: 'customerType',
          label: 'Customer Type',
          type: 'select',
          required: false,
          options: customerTypes,
          placeholder: 'Select customer type',
          description: 'Direct, Cocourier, Outstation, Branch',
        },
        {
          name: 'customerCategory',
          label: 'Customer Category',
          type: 'select',
          required: false,
          options: customerCategories,
          placeholder: 'Select customer category',
          description: 'Individual, Company, Partnership, LLP, Sole Proprietor',
        },
        {
          name: 'status',
          label: 'Status',
          type: 'radio',
          required: true,
          options: [
            { value: 'ACTIVE', label: 'Active' },
            { value: 'INACTIVE', label: 'Inactive' },
          ],
          description: 'Status of the Customer: Active or Inactive',
        },
      ],
    },
    {
      title: 'Company & Hub Information',
      description: 'Company and hub assignment details',
      fields: [
        {
          name: 'company',
          label: 'Company',
          type: 'select',
          required: false,
          options: companies.map((company: any) => ({
            value: company._id,
            label: `${company.name || 'Unknown Company'} (${company.code || 'N/A'})`,
          })),
          placeholder: 'Select a company',
          description: 'Link to Company Master',
        },
        {
          name: 'originHub',
          label: 'Origin Hub',
          type: 'select',
          required: false,
          options: hubs.map((hub: any) => ({
            value: hub._id,
            label: `${hub.name || 'Unknown Hub'} (${hub.code || 'N/A'})${hub.type ? ` - ${hub.type}` : ''}`,
          })),
          placeholder: 'Select an origin hub',
          description: 'Link to Hub Master',
        },
        {
          name: 'parentCustomer',
          label: 'Parent Customer',
          type: 'select',
          required: false,
          options: customerOptions
            .filter((customer: any) => customer._id !== editingCustomer?._id)
            .map((customer: any) => ({
              value: customer._id,
              label: `${customer.name || 'Unknown'} (${customer.code || 'N/A'})`,
            })),
          placeholder: 'Select parent customer',
          description: 'Select from existing customers (Co-couriers)',
        },
        {
          name: 'originCode',
          label: 'Origin Code (Country)',
          type: 'select',
          required: false,
          options: countries.map((country: any) => ({
            value: country._id,
            label: `${country.name || 'Unknown Country'} (${country.code || 'N/A'})`,
          })),
          placeholder: 'Select origin country',
          description: 'Link to Country Master',
        },
        {
          name: 'currency',
          label: 'Currency',
          type: 'select',
          required: false,
          options: currencies.map((currency: any) => ({
            value: currency._id,
            label: `${currency.name} (${currency.code}) - ${currency.symbol}`,
          })),
          placeholder: 'Select a currency',
          description: 'Link to Currency Master',
        },
      ],
    },
    {
      title: 'Sales & Operations Team',
      description: 'Assign sales and operations personnel to this customer',
      fields: [
        {
          name: 'salesman1',
          label: 'Salesman 1',
          type: 'select',
          required: false,
          options: userOptions,
          placeholder: 'Select salesman 1',
          description: 'Link to User Master',
        },
        {
          name: 'salesman2',
          label: 'Salesman 2',
          type: 'select',
          required: false,
          options: userOptions,
          placeholder: 'Select salesman 2',
          description: 'Link to User Master',
        },
        {
          name: 'salesman3',
          label: 'Salesman 3',
          type: 'select',
          required: false,
          options: userOptions,
          placeholder: 'Select salesman 3',
          description: 'Link to User Master',
        },
        {
          name: 'csPerson',
          label: 'CS Person',
          type: 'select',
          required: false,
          options: userOptions,
          placeholder: 'Select customer service person',
          description: 'Link to User Master',
        },
        {
          name: 'billingPerson',
          label: 'Billing Person',
          type: 'select',
          required: false,
          options: userOptions,
          placeholder: 'Select billing person',
          description: 'Link to User Master',
        },
        {
          name: 'invoicePerson',
          label: 'Invoice Person',
          type: 'select',
          required: false,
          options: userOptions,
          placeholder: 'Select invoice person',
          description: 'Link to User Master',
        },
      ],
    },
    {
      title: 'Email Configuration',
      description: 'Various email addresses for different purposes',
      fields: [
        {
          name: 'billingEmail',
          label: 'Billing Email',
          type: 'email',
          required: false,
          placeholder: 'Enter billing email address',
          description: 'Billing Email contact for the Customer',
        },
        {
          name: 'operationalEmail',
          label: 'Operational Email',
          type: 'email',
          required: false,
          placeholder: 'Enter operational email address',
          description: 'Operational Email contact for the Customer',
        },
        {
          name: 'misEmail',
          label: 'MIS Email',
          type: 'email',
          required: false,
          placeholder: 'Enter MIS email address',
          description: 'MIS Email contact for the Customer',
        },
        {
          name: 'misCcEmail',
          label: 'MIS CC Email',
          type: 'email',
          required: false,
          placeholder: 'Enter MIS CC email address',
          description: 'MIS CC Email contact for the Customer',
        },
      ],
    },
    {
      title: 'Billing & Payment Information',
      description: 'Billing cycle, invoice settings and payment configuration',
      fields: [
        {
          name: 'billingCycle',
          label: 'Billing Cycle',
          type: 'text',
          required: false,
          placeholder: 'Enter billing cycle (e.g., MONTHLY, WEEKLY)',
        },
        {
          name: 'invoiceHead',
          label: 'Invoice Head',
          type: 'radio',
          required: false,
          options: [
            { value: 'CASH_INVOICE', label: 'Cash Invoice' },
            { value: 'INVOICE', label: 'Invoice' },
          ],
          description: 'Status of the Invoice head',
        },
        {
          name: 'paymentType',
          label: 'Payment Type',
          type: 'select',
          required: false,
          options: paymentMethods,
          placeholder: 'Select payment type',
        },
        {
          name: 'adcNoc',
          label: 'ADC NOC',
          type: 'text',
          required: false,
          placeholder: 'Enter ADC NOC',
        },
        {
          name: 'tdsPercentage',
          label: 'TDS Percentage',
          type: 'number',
          min: 0,
          max: 100,
          placeholder: 'Enter TDS percentage',
          description: 'TDS percentage for the Customer',
        },
        {
          name: 'depositAmount',
          label: 'Deposit Amount',
          type: 'number',
          min: 0,
          placeholder: 'Enter deposit amount',
        },
        {
          name: 'creditLimit',
          label: 'Credit Limit',
          type: 'number',
          min: 0,
          placeholder: 'Enter credit limit',
        },
        {
          name: 'agingLimit',
          label: 'Aging Limit (Days)',
          type: 'number',
          min: 0,
          placeholder: 'Enter aging limit in days',
        },
      ],
    },
    {
      title: 'Legal & Tax Information',
      description: 'PAN, GST, EORI and other legal details',
      fields: [
        {
          name: 'pan',
          label: 'PAN Number',
          type: 'text',
          required: false,
          placeholder: 'Enter PAN number (e.g., ABCDE1234F)',
          description: 'PAN number of the Company',
        },
        {
          name: 'gstNumber',
          label: 'GST Number',
          type: 'text',
          required: false,
          placeholder: 'Enter GST number',
          description: 'GST number of the Customer',
        },
        {
          name: 'eoriNumber',
          label: 'EORI Number',
          type: 'text',
          required: false,
          placeholder: 'Enter EORI number',
          description: 'EORI number of the Customer',
        },
        {
          name: 'iec',
          label: 'IEC Number',
          type: 'text',
          required: false,
          placeholder: 'Enter IEC number',
          description: 'IEC number of the Customer',
        },
        {
          name: 'iecBranchCode',
          label: 'IEC Branch Code',
          type: 'text',
          required: false,
          placeholder: 'Enter IEC branch code',
          description: 'IEC branch code of the Customer',
        },
      ],
    },
    {
      title: 'Banking Information',
      description: 'Bank account details and AD code',
      fields: [
        {
          name: 'bankAccountNumber',
          label: 'Bank Account Number',
          type: 'text',
          required: false,
          placeholder: 'Enter bank account number',
          description: 'Bank account number of the Customer',
        },
        {
          name: 'bankAdCode',
          label: 'Bank AD Code',
          type: 'text',
          required: false,
          placeholder: 'Enter bank AD code',
          description: 'Bank AD code of the Customer',
        },
      ],
    },
    {
      title: 'Address Information',
      description: 'Customer address and location details',
      fields: [
        {
          name: 'address',
          label: 'Address',
          type: 'textarea',
          placeholder: 'Enter full address',
          rows: 3,
        },
        {
          name: 'city',
          label: 'City',
          type: 'text',
          placeholder: 'Enter city',
        },
        {
          name: 'state',
          label: 'State',
          type: 'text',
          placeholder: 'Enter state',
        },
        {
          name: 'country',
          label: 'Country',
          type: 'text',
          placeholder: 'Enter country',
        },
        {
          name: 'pincode',
          label: 'Pincode',
          type: 'text',
          placeholder: 'Enter pincode',
        },
        {
          name: 'placeOfSupply',
          label: 'Place of Supply',
          type: 'radio',
          required: false,
          options: [
            { value: 'SAME', label: 'Same' },
            { value: 'DIFFERENT', label: 'Different' },
          ],
          description: 'Status of the Place of supply: Same or Different',
        },
      ],
    },
    {
      title: 'Hub Assignment',
      description: 'Assign multiple hubs to this customer',
      fields: [
        {
          name: 'assignedHubs',
          label: 'Assigned Hubs',
          type: 'multiselect',
          required: false,
          options: hubOptions,
          placeholder: 'Select hubs to assign',
          description: 'Select all the hubs this customer can operate with',
        },
      ],
    },
    {
      title: 'Documents & Contracts',
      description: 'Upload contract and rate modifier documents',
      fields: [
        {
          name: 'contract',
          label: 'Contract',
          type: 'file',
          required: false,
          accept: '.pdf,.doc,.docx,.jpg,.jpeg,.png',
          placeholder: 'Upload contract file (PDF, DOC, DOCX, JPG, PNG)',
          description: 'Contract of the customer to be uploaded',
          currentFileUrl: editingCustomer?.contract ? 'file-exists' : undefined,
          onDownloadFile: editingCustomer?.contract 
            ? () => downloadCustomerFile(editingCustomer, 'contract') 
            : undefined,
        },
        {
          name: 'rateModifierHead',
          label: 'Rate Modifier Head',
          type: 'file',
          required: false,
          accept: '.pdf,.xls,.xlsx,.csv',
          placeholder: 'Upload rate modifier file (PDF, XLS, XLSX, CSV)',
          description: 'Rate modifier of the customer to be uploaded',
          currentFileUrl: editingCustomer?.rateModifierHead ? 'file-exists' : undefined,
          onDownloadFile: editingCustomer?.rateModifierHead 
            ? () => downloadCustomerFile(editingCustomer, 'rateModifierHead') 
            : undefined,
        },
      ],
    },
    {
      title: 'KYC Documents',
      description: 'Upload KYC documents for verification',
      fields: kycTypes.length > 0 
        ? kycTypes.map((kyc: any) => ({
            name: `kycDocument_${kyc._id}`,
            label: kyc.name,
            type: 'file' as const,
            required: false,
            accept: '.pdf,.jpg,.jpeg,.png',
            placeholder: `Upload ${kyc.name} (PDF, JPG, PNG)`,
            description: `${kyc.name} - ${kyc.code}`,
          }))
        : [{
            name: 'kycDocuments',
            label: 'KYC Documents',
            type: 'text' as const,
            disabled: true,
            placeholder: 'No KYC types available. Please add KYC types first.',
            description: 'KYC types will appear here once configured in KYC Master',
          }],
    },
    {
      title: 'Additional Information',
      description: 'Customer instructions and other notes',
      fields: [
        {
          name: 'customerInstruction',
          label: 'Customer Instructions',
          type: 'textarea',
          placeholder: 'Enter any special instructions for this customer',
          rows: 3,
        },
      ],
    },
  ];

  const formSections = getFormSections();

  const handleAdd = () => {
    setEditingCustomer(null);
    setIsFormOpen(true);
  };

  // Transform customer data for editing - extract IDs from populated object fields
  const transformCustomerForEdit = (customer: Customer) => {
    // Helper function to extract ID from object or return string as-is
    const extractId = (value: any) => {
      if (typeof value === 'object' && value !== null && value._id) {
        return value._id;
      }
      return value;
    };

    // Transform assignedHubs array - extract IDs from objects
    const transformedHubs = customer.assignedHubs?.map((hub: any) => {
      if (typeof hub === 'object' && hub !== null && hub._id) {
        return hub._id;
      }
      return hub;
    }) || [];

    return {
      ...customer,
      // Extract _id from object fields if they are populated
      company: extractId(customer.company),
      originHub: extractId(customer.originHub),
      parentCustomer: extractId(customer.parentCustomer),
      originCode: extractId(customer.originCode),
      currency: extractId(customer.currency),
      salesman1: extractId(customer.salesman1),
      salesman2: extractId(customer.salesman2),
      salesman3: extractId(customer.salesman3),
      csPerson: extractId(customer.csPerson),
      billingPerson: extractId(customer.billingPerson),
      invoicePerson: extractId(customer.invoicePerson),
      // Transform array fields
      assignedHubs: transformedHubs,
    };
  };

  const handleEdit = (customer: Customer) => {
    setEditingCustomer(transformCustomerForEdit(customer) as Customer);
    setIsFormOpen(true);
  };

  const handleDelete = (customer: Customer) => {
    deleteCustomerMutation.mutate(customer._id);
  };

  const handleBulkDelete = (customers: Customer[]) => {
    const customerIds = customers.map(c => c._id);
    if (!customerIds.length) return;
    bulkDeleteMutation.mutate(customerIds);
  };

  const handleFormSubmit = async (data: any) => {
    // Separate file fields from other data
    const { contract, rateModifierHead, ...restData } = data;
    
    // Extract KYC document files (fields starting with kycDocument_)
    const kycFiles: Record<string, File> = {};
    const kycDocumentIds: string[] = [];
    
    Object.keys(restData).forEach(key => {
      if (key.startsWith('kycDocument_')) {
        if (restData[key] instanceof File) {
          kycFiles[key] = restData[key];
          const kycId = key.replace('kycDocument_', '');
          kycDocumentIds.push(kycId);
        } else if (typeof restData[key] === 'string' && restData[key]) {
          // Existing file URL
          const kycId = key.replace('kycDocument_', '');
          kycDocumentIds.push(kycId);
        }
        delete restData[key];
      }
    });

    // Transform form data to match backend expectations
    const transformedData: any = {
      ...restData,
      // Ensure required fields are present with defaults if needed
      tdsPercentage: restData.tdsPercentage || 0,
      depositAmount: restData.depositAmount || 0,
      creditLimit: restData.creditLimit || 0,
      agingLimit: restData.agingLimit || 0,
      // If no hubs assigned, use origin hub as default
      assignedHubs: Array.isArray(restData.assignedHubs) && restData.assignedHubs.length > 0 
        ? restData.assignedHubs 
        : [restData.originHub].filter(Boolean),
      kycDocuments: kycDocumentIds.length > 0 ? kycDocumentIds : (editingCustomer?.kycDocuments || []),
    };

    // Handle file uploads
    const hasFileUploads = 
      (contract instanceof File) || 
      (rateModifierHead instanceof File) || 
      Object.keys(kycFiles).length > 0;

    // Clean up undefined values
    Object.keys(transformedData).forEach(key => {
      if (transformedData[key] === undefined) {
        delete transformedData[key];
      }
    });

    // If there are file uploads, use FormData
    if (hasFileUploads) {
      const formData = new FormData();
      
      // Append all non-file data
      Object.keys(transformedData).forEach(key => {
        const value = transformedData[key];
        if (Array.isArray(value)) {
          // Handle arrays properly for FormData
          value.forEach((item, index) => {
            formData.append(`${key}[${index}]`, item);
          });
        } else if (value !== null && value !== undefined) {
          formData.append(key, typeof value === 'object' ? JSON.stringify(value) : String(value));
        }
      });

      // Append files
      if (contract instanceof File) {
        formData.append('contract', contract);
      }
      if (rateModifierHead instanceof File) {
        formData.append('rateModifierHead', rateModifierHead);
      }
      
      // Append KYC files
      Object.entries(kycFiles).forEach(([key, file]) => {
        formData.append(key, file);
      });

      // Note: The backend needs to handle multipart/form-data
      // For now, we'll send JSON and handle files separately if needed
      if (editingCustomer) {
        updateCustomerMutation.mutate({
          ...transformedData,
          _id: editingCustomer._id,
        });
      } else {
        createCustomerMutation.mutate(transformedData);
      }
    } else {
      if (editingCustomer) {
        updateCustomerMutation.mutate({
          ...transformedData,
          _id: editingCustomer._id,
        });
      } else {
        createCustomerMutation.mutate(transformedData);
      }
    }
  };

  const handleExport = () => {
    const params: Record<string, string> = {};
    const normalizedSearch = searchTerm.trim();
    if (normalizedSearch) {
      params.search = normalizedSearch;
    }

    Object.entries(currentFilters).forEach(([key, value]) => {
      if (!value) return;
      if (value === 'all') {
        if (key === 'status') {
          params[key] = value;
        }
        return;
      }
      params[key] = value;
    });

    exportData('/api/master-data/customer/export', 'customers', 'csv', {
      params,
      emptyMessage: 'No customers match the current filters.',
      transform: (payload) => {
        const data = Array.isArray(payload) ? payload : payload?.data || [];
        if (!data.length) return null;

        const headers = [
          'Name',
          'Code',
          'Email',
          'Contact',
          'Company',
          'Type',
          'Category',
          'Status',
          'City',
          'State',
          'Country',
          'GST Number',
          'Created Date',
        ];

        const rows = data.map((customer: any) => [
          customer.name || '',
          customer.code || '',
          customer.email || '',
          customer.contactNo || '',
          typeof customer.company === 'object' ? customer.company?.name || '' : customer.company || '',
          customerTypeLabelMap[customer.customerType] || customer.customerType || '',
          customerCategoryLabelMap[customer.customerCategory] || customer.customerCategory || '',
          customer.status === 'ACTIVE' ? 'Active' : customer.status || '',
          customer.city || '',
          customer.state || '',
          customer.country || '',
          customer.gstNumber || '',
          customer.createdAt ? new Date(customer.createdAt).toLocaleDateString() : '',
        ]);

        return {
          content: buildCsv(headers, rows, { includeBom: true, quoteStrings: true }),
          mimeType: 'text/csv;charset=utf-8;',
          extension: 'csv',
        };
      },
    });
  };

  const handleRefresh = () => {
    queryClient.invalidateQueries({ queryKey: ['customers'] });
  };

  const handleImport = (file: File) => {
    importData(file, '/api/master-data/customer/import', () => {
      queryClient.invalidateQueries({ queryKey: ['customers'] });
      queryClient.invalidateQueries({ queryKey: ['customers-dropdown'] });
      setCurrentPage(1);
    });
  };

  return (
    <>
      <ImportExportProgress
        type='import'
        isActive={importProgress.isImporting}
        progress={importProgress.progress}
        message={importProgress.message}
        errors={importProgress.errors}
      />

      <ImportExportProgress
        type='export'
        isActive={exportProgress.isExporting}
        progress={exportProgress.progress}
        message={exportProgress.message}
      />

      {error && (
        <div className='mb-4 p-4 bg-red-50 border border-red-200 rounded-md'>
          <p className='text-red-800'>
            Error loading customers: {error.message}
          </p>
        </div>
      )}

      <MasterDataTable
        title='Customer Management'
        description='Manage customer information and relationships'
        data={customers}
        columns={columns}
        isLoading={isLoading}
        // Pagination (server-side)
        serverSide={true}
        pagination={pagination}
        onPageChange={setCurrentPage}
        onPageSizeChange={setPageSize}
        // Search
        currentSearch={searchTerm}
        onSearchChange={setSearchTerm}
        // Filtering
        currentFilters={currentFilters}
        onFiltersChange={setCurrentFilters}
        filters={filters}
        // Sorting
        currentSort={sortConfig}
        onSortChange={setSortConfig}
        // Actions
        onRefresh={handleRefresh}
        deleteConfirm
        onAdd={handleAdd}
        onEdit={handleEdit}
        onDelete={handleDelete}
        onBulkDelete={handleBulkDelete}
        onExport={handleExport}
        onImport={handleImport}
        searchPlaceholder='Search customers by name, email, or code...'
        allowBulkOperations={true}
        allowImportExport={true}
      />

      <MasterDataForm
        title={editingCustomer ? 'Edit Customer' : 'Add New Customer'}
        description={
          editingCustomer
            ? 'Update customer information and settings.'
            : 'Create a new customer account with all necessary details.'
        }
        sections={formSections}
        isOpen={isFormOpen}
        onClose={() => {
          setIsFormOpen(false);
          setEditingCustomer(null);
        }}
        onSubmit={handleFormSubmit}
        isLoading={
          createCustomerMutation.isPending || updateCustomerMutation.isPending
        }
        initialData={editingCustomer}
        mode={editingCustomer ? 'edit' : 'create'}
        entityName="Customer"
      />
    </>
  );
};

export default CustomerManagement;
